import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AppErrors, NotFoundError } from '@fitness-force/models';;
import type { STAFFINTERFACE } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { environment } from '@fitness-force/environments';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
import { selectStaffList, selectStaffById, updateStaffList, addStaffMember } from '@fitness-force/state';
import {
  catchError,
  map,
  Observable,
  of,
  switchMap,
  take,
  throwError,
  shareReplay
} from 'rxjs';


@Injectable({
  providedIn: 'root',
})
export class StaffStoreService {
  private http = inject(HttpClient);
  private store = inject(Store);


  private BASE_URL: string = environment.BASE_URL;
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };
  
  // Cache to store staff requests
  private staffCache: Map<number, Observable<STAFFINTERFACE>> = new Map();
  
  // Get staff by ID (checks store first, then API if not found)
  getStaffById(staffId: number): Observable<STAFFINTERFACE> {
    // Check if request is already in cache
    const cachedRequest = this.staffCache.get(staffId);
    if (cachedRequest) {
      return cachedRequest;
    }

    // if(staffId == 0){
    //   return of(null as any);
    // }

    // If not in cache, create new request
    const request = this.store.select(selectStaffById(staffId)).pipe(
      take(1),
      switchMap((staffFromStore) => {
        if (staffFromStore) {
          return of(staffFromStore);
        }

        // If not in store, fetch from API
        return this.GetStaffById(staffId).pipe(
          map((staff) => {
            // Add to store
            this.store.dispatch(addStaffMember({ staff }));
            return staff;
          })
        );
      }),
      // Share the response with all subscribers and cache it
      shareReplay(1)
    );

    // Store in cache
    this.staffCache.set(staffId, request);
    return request;
  }

  private GetStaffById(STAFF_ID: number): Observable<STAFFINTERFACE> {
    const url = `${this.BASE_URL}${CONST_URL_SEGEMENTS.STAFF.GET_STAFF_LIST}/${STAFF_ID}`;
    return this.http
      .get<STAFFINTERFACE>(url, { headers: this.httpOptions.headers })
      .pipe(
        map((res) => {
          if (res) {
            return res;
          }
          throw new NotFoundError('Staff member not found');
        }),
        catchError((error: any) => {
          // Handle network errors (status 0)
          if (!error.status) {
            return throwError(() => new AppErrors('Network error or server not responding. Please check your connection.'));
          }
          // Handle 404
          if (error.status === 404) {
            return throwError(() => new NotFoundError('Staff member not found'));
          }
          // Handle other errors
          return throwError(() => new AppErrors(`Error fetching staff member: ${error.message || 'Unknown error'}`));
        })
      );
  }
}
